import {
  SizeChartWithDistribution,
  SizeKeyDistribution,
  RecommendedSizeChartsResponse,
  RecommendedDistribution,
} from '../../models/rest/sizing';
import { SizeChart, SizeKey, SizingKey, SizingResult } from '../../models/sizing';
import { mapRestDistributionToStore } from './distribution-adapter';

function mapRestSizeKeyToStore(restKey: SizeKeyDistribution): SizeKey {
  const dist = mapRestDistributionToStore(restKey.recommended_distribution ?? null);
  return {
    manualSizeKey: false,
    sizeKey: restKey.size_key,
    sizeKeyId: restKey.size_key.join(';'),
    sizeList: restKey.size_list,
    lengthList: restKey.length_list,
    configCount: restKey.silo_config_count,
    distribution: dist?.distribution,
    distributionType: dist?.distributionType,
    recommendationStrategy: dist?.recoStrategy,
    recommendationLevel: dist?.recoLevel,
  };
}

function mapRestSizeChartToStore(restChart: SizeChartWithDistribution): SizeChart {
  return {
    sizeChartId: restChart.size_chart_id,
    sizeChartCode: restChart.size_chart_code,
    sizeList: restChart.size_list,
    lengthChartCode: restChart.length_chart_code ?? undefined,
    lengthList: restChart.length_list,
    sizeLengthSeparator: restChart.size_length_separator ?? undefined,
    description: restChart.description ?? '',
    sizeKeys: restChart.size_keys.map(mapRestSizeKeyToStore),
  };
}

/**
 * Maps size charts (without distribution) plus a separately-fetched distribution to a SizingResult.
 *
 * Used by PT-51851 for sized groups: fetches charts and distribution in parallel, then merges them.
 * The distribution is injected into the matching size key identified by sizeChartCode + sizeKeys.
 * The group's existing chart/key selection is preserved as both recommended and selected.
 *
 * @param chartsResponse REST response from GET /recommended-size-charts
 * @param distribution REST response from GET /recommended-size-distribution
 * @param groupId Group identifier
 * @param sizeChartCode The group's existing size chart code (e.g. "CHART_EU")
 * @param sizeKeys The group's existing size keys joined by ';' (e.g. "S;M;L")
 */
export function mapRestSizeChartsWithSeparateDistribution(
  chartsResponse: RecommendedSizeChartsResponse,
  distribution: RecommendedDistribution,
  groupId: string,
  sizeChartCode: string,
  sizeKeys: string
): SizingResult {
  const enrichedResponse: RecommendedSizeChartsResponse = {
    size_charts: chartsResponse.size_charts.map((chart) => ({
      ...chart,
      size_keys: chart.size_keys.map((key) =>
        chart.size_chart_code === sizeChartCode && key.size_key.join(';') === sizeKeys
          ? { ...key, recommended_distribution: distribution }
          : key
      ),
    })),
  };

  const result = mapRestSizeChartsToSizingResult(enrichedResponse, groupId);

  const existingChart = result.sizeChartList.find((sc) => sc.sizeChartCode === sizeChartCode);
  const existingKey = existingChart?.sizeKeys.find((sk) => sk.sizeKeyId === sizeKeys);
  if (existingChart && existingKey) {
    const existingSelection: SizingKey = { sizeChartId: existingChart.sizeChartId, sizeKeyId: existingKey.sizeKeyId };
    return { ...result, selectedSizing: existingSelection, recommendedSizing: existingSelection };
  }

  return result;
}

/**
 * Maps REST size charts response to a SizingResult for a given group.
 *
 * selectedSizing and recommendedSizing are set to the same value on initial load because
 * no manual chart/key selection has been made yet. Once the user picks a different
 * chart or key, the store updates selectedSizing while recommendedSizing stays fixed
 * as the algorithm's suggestion.
 *
 * @param response REST response from GET /recommended-size-charts-with-distribution
 * @param groupId Group identifier (composite string key from REST)
 * @returns SizingResult ready for store consumption
 */
export function mapRestSizeChartsToSizingResult(
  response: RecommendedSizeChartsResponse,
  groupId: string
): SizingResult {
  const sizeChartList = response.size_charts.map(mapRestSizeChartToStore);

  // Find first chart+key that has a distribution (recommended sizing)
  let recommendedSizing: SizingKey = { sizeChartId: '', sizeKeyId: '' };
  let selectedSizing: SizingKey = { sizeChartId: '', sizeKeyId: '' };

  outer: for (const chart of sizeChartList) {
    for (const key of chart.sizeKeys) {
      if (key.distribution) {
        recommendedSizing = { sizeChartId: chart.sizeChartId, sizeKeyId: key.sizeKeyId };
        selectedSizing = { sizeChartId: chart.sizeChartId, sizeKeyId: key.sizeKeyId };
        break outer;
      }
    }
  }

  // Fall back to first chart+key if none have distribution
  if (!recommendedSizing.sizeChartId && sizeChartList.length > 0 && sizeChartList[0].sizeKeys.length > 0) {
    const firstChart = sizeChartList[0];
    const firstKey = firstChart.sizeKeys[0];
    recommendedSizing = { sizeChartId: firstChart.sizeChartId, sizeKeyId: firstKey.sizeKeyId };
    selectedSizing = { sizeChartId: firstChart.sizeChartId, sizeKeyId: firstKey.sizeKeyId };
  }

  return {
    groupId,
    selectedSizing,
    recommendedSizing,
    sizeChartList,
    sizingDecisions: [],
  };
}
